import { memo, useCallback } from "react";
import { motion } from "framer-motion";
import { HiOutlineCheck } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { QuestionItem } from "types/assessmentTypes";

type QuestionProps = {
  question: QuestionItem;
  selectedOption?: number;
  onOptionSelect: (questionId: number, optionId: number | null) => void;
  index: number;
  description?: string;
  onDescriptionChange?: (questionId: number, description: string) => void;
  type?: "company" | "organization";
};

const Question = memo(function Question({
  question,
  selectedOption,
  onOptionSelect,
  index,
  description = "",
  onDescriptionChange,
  type,
}: QuestionProps) {
  const { t } = useTranslation();

  const handleSelect = useCallback(
    (optionId: number) => {
      if (selectedOption === optionId) {
        onOptionSelect(question.id, null);
      } else {
        onOptionSelect(question.id, optionId);
      }
    },
    [onOptionSelect, question.id, selectedOption]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onDescriptionChange) {
        onDescriptionChange(question.id, e.target.value);
      }
    },
    [onDescriptionChange, question.id]
  );

  return (
    <div className="group rounded-xl border border-gray-200 bg-white/80 p-4 transition-all duration-200 hover:border-brand-200 hover:shadow-md dark:border-gray-700/50 dark:bg-navy-800/90 dark:hover:border-brand-500/30 sm:p-5">
      <h3 className="mb-4 text-start text-sm font-bold leading-relaxed text-gray-800 dark:text-white sm:mb-5 sm:text-base">
        {index + 1} -{" "}
        {type === "company" ? t(`question.${question.index}`) : question.name}
      </h3>

      <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
        {[...question.options]
          .sort((a, b) => a?.order - b?.order)
          .map((option, index) => {
            const isSelected = selectedOption === option.id;

            return (
              <motion.button
                key={option.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: Math.min(index * 0.03, 0.15) }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleSelect(option.id)}
                className={`
                relative w-full rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200
                sm:flex-1
                ${
                  isSelected
                    ? "bg-gradient-to-r from-brand-500 to-brand-600 text-white shadow-lg shadow-brand-500/30"
                    : "border border-gray-200 bg-white text-gray-700 hover:border-brand-300 hover:bg-brand-50 dark:border-gray-700 dark:bg-navy-800 dark:text-gray-300 dark:hover:border-brand-500/50 dark:hover:bg-brand-500/10"
                }
              `}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {isSelected && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        duration: 0.15,
                      }}
                    >
                      <HiOutlineCheck className="h-4 w-4" />
                    </motion.span>
                  )}
                  <span className="whitespace-normal break-words text-center">
                    {t(`option.${option.key}`)}
                  </span>
                </span>
              </motion.button>
            );
          })}
      </div>

      {/* Description textarea for organization assessments */}
      {type === "organization" && onDescriptionChange && (
        <div className="mt-4">
          <textarea
            value={description}
            onChange={handleDescriptionChange}
            placeholder={t("assessment.descriptionPlaceholder")}
            className="w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
            rows={2}
          />
        </div>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        {selectedOption ? (
          <motion.span
            initial={{ opacity: 0, x: 5 }}
            animate={{ opacity: 1, x: 0 }}
            className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700 dark:bg-green-500/20 dark:text-green-400"
          >
            <HiOutlineCheck className="h-3 w-3" />
            {t("assessment.answered")}
          </motion.span>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {t("assessment.notAnswered")}
          </span>
        )}
      </div>
    </div>
  );
});

export default Question;
